import {useInfiniteQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} SiteListItem
 * @property {number} id
 * @property {number} schoolId
 * @property {string} nationalSchoolId
 * @property {string} name
 * @property {string} phoneNumber
 * @property {string} website
 * @property {string} type
 * @property {string} addressLine1
 * @property {string} addressLine2
 * @property {string} addressLine3
 * @property {string} city
 * @property {string} state
 * @property {string} zipCode
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {boolean} isActive
 * @property {number} networkId
 * @property {number} networkCount
 */

/**
 * @typedef {Object} SiteListResponse
 * @property {SiteListItem[]} data
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total - total updates available
 * @property {number} overallTotal - total sites available
 * @property {string} lastUpdated - ISO date string of last update
 * @property {string} schemaVersion - schema version of the data
 */

const STALE_TIME = 60 * 60 * 1000; // 1 hour

/**
 * Custom hook to fetch site leads from the core API.
 *
 * @param {Object} params - Query parameters for the request.
 * @param {number} [params.pageSize=2000] - The number of items per page.
 * @param {string|null} [params.lastUpdated=null] - ISO date string of last update.
 * @param {Object} options - Configuration options like 'enabled' to control the query.
 * @param {boolean} options.enabled - If false, the query will not execute automatically. This is useful for delaying
 * the fetch until specific criteria are met (e.g., waiting for IndexedDB to be ready).
 *
 * @returns {import('@tanstack/react-query').UseInfiniteQueryResult<SiteListResponse>} React Query result object.
 */
export default function useCoreSiteList(
  {pageSize = 2000, lastUpdated = null} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useInfiniteQuery({
    queryKey: ['site', 'core', 'list', pageSize, lastUpdated],

    queryFn: ({pageParam = 1}) => {
      const url = new URL(`${apiRoot}/core/sites`);

      // Add query parameters
      url.searchParams.append('page', String(pageParam));
      url.searchParams.append('pageSize', String(pageSize));
      if (lastUpdated != null) url.searchParams.append('lastUpdated', lastUpdated);

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch site list`);
        }
        return res.json();
      });
    },

    getNextPageParam: (lastPage) => {
      const {page, pageSize, total, data} = lastPage;
      return data.length >= pageSize ? page + 1 : undefined;
    },

    getPreviousPageParam: (firstPage) => {
      const {page} = firstPage;
      return page > 1 ? page - 1 : undefined;
    },

    initialPageParam: 1,
    staleTime: STALE_TIME,
    enabled: enabled,
  });

  return {
    ...query,
  };
}
